import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Grid,
  Avatar,
  Chip,
  TextField,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { updateUser, getAllUser } from '../../api/user-api';
import UserManagementPage from '../UsersRoleManagement';
import UserStats from './UserStats'; 


const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [openUserDialog, setOpenUserDialog] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const userData = await getAllUser();
        setUsers(userData || []);
      } catch (error) {
        console.error("Error fetching users:", error.message);
      }
    };

    fetchUsers();
  }, []);

  const handleApproval = async (id, approvalStatus) => {
    try {
      const updatedUser = await updateUser(id, { status: approvalStatus });
      setUsers(users.map(user =>
        user.id === id ? { ...user, status: approvalStatus } : user
      ));
    } catch (error) {
      console.error("Error updating user:", error.message);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCloseUserDialog = () => {
    setOpenUserDialog(false);
    setSelectedUser(null);
  };

  const filteredUsers = users?.filter(user =>
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPendingUsers = users?.filter(user => user.status === 'pending').length;
  const totalApprovedUsers = users?.filter(user => user.status === 'approved').length;
  const totalUsers = users.length;

  return (
    <Container maxWidth="xl" sx={{ py: 10 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 'bold', color:" #1976d2"}}>
          Admin Dashboard
        </Typography>
        <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
          Manage user approvals, view uploaded data details, and manage posts
        </Typography>
      </Box>

      {/* Summary Cards */}
      <UserStats
        totalPendingUsers={totalPendingUsers}
        totalApprovedUsers={totalApprovedUsers}
        totalUsers={totalUsers}
        user={users}
      />
      {/* Search Box */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <TextField
          placeholder="Search by name or email"
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* User Table */}
      <TableContainer component={Paper} elevation={3} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((user) => (
              <TableRow key={user.id}>
                <TableCell>
                  <Grid container alignItems="center">
                    <Grid item>
                      <Avatar sx={{ mr: 2, bgcolor: " #1976d2", color: 'white' }}>
                        {user.name[0]}
                      </Avatar>
                    </Grid>
                    <Grid item>{user.name}</Grid>
                  </Grid>
                </TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell>
                  <Chip
                    label={user.status}
                    color={user.status === 'approved' ? 'success' : 'warning'}
                    icon={user.status === 'approved' ? <CheckIcon /> : <CloseIcon />}
                    sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}
                  />
                </TableCell>
                {/* Here we now show the role instead of details */}
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {user.role || 'No role assigned'}
                  </Typography>
                </TableCell>
                <TableCell>
                  {user.status === 'pending' && (
                    <>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{ mr: 1 }}
                        onClick={() => handleApproval(user.id, 'approved')}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleApproval(user.id, 'rejected')}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {user.status === 'approved' && (
                    <Typography variant="body2" color="text.secondary">
                      Approved
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>


        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* User Details Dialog */}
      {selectedUser && (
        <Dialog open={openUserDialog} onClose={handleCloseUserDialog}>
          <DialogTitle>User Details</DialogTitle>
          <DialogContent>
            <Typography variant="h6">{selectedUser.name}</Typography>
            <Typography variant="body1">Email: {selectedUser.email}</Typography>
            <Typography variant="body1">Status: {selectedUser.status}</Typography>
            <Typography variant="body1">Details: {selectedUser.details}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseUserDialog}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      <UserManagementPage/>
    </Container>
  );
};

export default AdminPage;
